import { detectionsApi } from '@/api/detections.api';
import type { DetectionModel, HoleSet } from '@/types/detection.types';
import { Eye, Trash2 } from 'lucide-react';
import { Button } from 'primereact/button';
import type { FC } from 'react';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { ConfirmDeleteDialog } from '../dialogs/ConfirmDeleteDialog';
import { JSONViewDialog } from '../dialogs/JSONViewDialog';

interface DetectionActionsProps {
  detection: DetectionModel;
  onDelete: (id: string) => void;
}

export const DetectionActions: FC<DetectionActionsProps> = ({ detection, onDelete }) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSourceDialog, setShowSourceDialog] = useState(false);
  const [sourceData, setSourceData] = useState<HoleSet | null>(null);
  const [isLoadingSource, setIsLoadingSource] = useState(false);

  const handleViewSource = async () => {
    setIsLoadingSource(true);
    try {
      const data = await detectionsApi.getSource(detection.id);
      setSourceData(data);
      setShowSourceDialog(true);
    } catch (_error) {
      toast.error('Failed to load holes data');
    } finally {
      setIsLoadingSource(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    onDelete(detection.id);
    setShowDeleteDialog(false);
  };

  return (
    <>
      <div className="flex gap-1 items-center">
        {/* View source (HoleSet for this orientation) */}
        <Button
          icon={<Eye size={16} />}
          onClick={handleViewSource}
          text={true}
          rounded={true}
          size="small"
          loading={isLoadingSource}
          tooltip="View holes for this orientation"
          tooltipOptions={{ position: 'top' }}
          aria-label="View holes"
        />

        {/* Delete */}
        <Button
          icon={<Trash2 size={16} />}
          onClick={handleDelete}
          text={true}
          rounded={true}
          size="small"
          severity="danger"
          tooltip="Delete detection"
          tooltipOptions={{ position: 'top' }}
          aria-label="Delete"
        />
      </div>

      {/* Delete confirmation dialog */}
      <ConfirmDeleteDialog
        visible={showDeleteDialog}
        title="Delete Detection"
        message={`Are you sure you want to delete detection for orientation "${detection.orientation}"? This action cannot be undone.`}
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteDialog(false)}
      />

      {/* Source JSON dialog (HoleSet for this orientation) */}
      {sourceData && (
        <JSONViewDialog
          visible={showSourceDialog}
          title={`Holes: ${detection.orientation}`}
          jsonData={sourceData}
          onClose={() => setShowSourceDialog(false)}
          downloadFilename={`detection_${detection.orientation}_holes.json`}
        />
      )}
    </>
  );
};
